import { Link } from 'react-router-dom';
import * as AlertNotification from '@givebutter/primitives/alert-notification';
import { BoxIcon } from '@givebutter/primitives/box-icon';
import { Button } from '@givebutter/primitives/button';
import { maskDecimal } from '@givebutter/util';
import { faSackDollar } from '@fortawesome/pro-regular-svg-icons';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    useOveragesShouldWarnBillingChecks,
    usePlanOverageUsageData,
} from './usePlanOverageUsageData';
import { UpdateToRecommendedPlanButton } from 'assets/js/pages/account-settings/account-about/givebutter-plus/UpdateToRecommendedPlanButton';
import { useResetOverageWarningStoresWhenResolved } from 'assets/js/pages/account-settings/account-about/givebutter-plus/useResetOverageWarningStoresWhenResolved';
import { AnchorHashIds } from 'constants/anchor-hash-ids';
import { posthogEvents } from 'constants/posthog-events';
import { useAccountLocalStorage } from 'contexts/useAccountLocalStorage';
import { CurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { usePosthog } from 'customHooks/usePosthog';
import { useRecommendedPlanComparison } from 'customHooks/useRecommendedPlanComparison';
import { LONG_DATE_FORMAT, formatDate, isoDateFromServer } from 'helpers/dates';

const OveragesWarningPrompt = () => {
    const { validGivebutterPlusPlan, accountId } = useCurrentAccount();
    const { recommendedPlanDifference } = useRecommendedPlanComparison();
    const { isGracePeriod } = usePlanOverageUsageData();
    const { shouldPromptToManagePlans } = useOveragesShouldWarnBillingChecks();
    const { pushEvent } = usePosthog();
    useResetOverageWarningStoresWhenResolved();

    const {
        setHasDismissedOveragesWarningGracePeriodBilling,
        setHasDismissedOveragesWarningBilling,
    } = useAccountLocalStorage(
        ({
            setHasDismissedOveragesWarningGracePeriodBilling,
            setHasDismissedOveragesWarningBilling,
        }) => {
            return {
                setHasDismissedOveragesWarningGracePeriodBilling,
                setHasDismissedOveragesWarningBilling,
            };
        },
    );

    if (!validGivebutterPlusPlan || !shouldPromptToManagePlans) {
        return null;
    }

    const { tier, overage_reporting_starting_at: overageReportingStartingAt } =
        validGivebutterPlusPlan;

    return (
        <AlertNotification.Root type="info" variant="solid" className="relative">
            <BoxIcon size="md">
                <FontAwesomeIcon
                    icon={faSackDollar}
                    size="lg"
                    className="text-brand-cobalt-primary"
                />
            </BoxIcon>
            <div className="flex flex-col gap-2 text-sm">
                <div>
                    <p className="font-medium">
                        You could save <CurrencySymbol />
                        {maskDecimal(recommendedPlanDifference)}{' '}
                        {tier.interval === 'month' ? '/mo' : '/year'} in overages by updating your
                        Givebutter Plus plan.
                    </p>
                    <p className="text-gray-600">
                        {isGracePeriod && overageReportingStartingAt
                            ? `Overage charges will start on ${formatDate(isoDateFromServer(overageReportingStartingAt), LONG_DATE_FORMAT)}. Consider updating to a higher-tier plan to avoid overage charges.`
                            : 'You’re spending more in overage charges than the cost of the recommended plan. Consider updating to a higher-tier plan to avoid overage charges.'}
                    </p>
                </div>
                <div className="flex gap-4 font-medium">
                    <Link
                        to={`/accounts/${accountId}/settings/about/subscription#${AnchorHashIds.ManageGivebutterPlusPlan}`}
                    >
                        <Button
                            variant="none"
                            color="neutral"
                            className="text-gray-700"
                            onClick={() => {
                                pushEvent(
                                    posthogEvents.DASHBOARD_OVERAGES_BILLING_BANNER_SELECTED,
                                    {
                                        isGracePeriod,
                                        text: 'View Plans',
                                    },
                                );
                            }}
                        >
                            View plans
                        </Button>
                    </Link>
                    <UpdateToRecommendedPlanButton />
                </div>
            </div>
            <Button
                variant="transparent"
                size="sm"
                className="absolute right-1 top-1 text-gray-500 hover:bg-gray-100"
                onClick={() => {
                    if (isGracePeriod) {
                        setHasDismissedOveragesWarningGracePeriodBilling(true);
                    } else {
                        setHasDismissedOveragesWarningBilling(true);
                    }
                    pushEvent(posthogEvents.DASHBOARD_OVERAGES_BILLING_BANNER_DISMISSED, {
                        isGracePeriod,
                    });
                }}
            >
                <FontAwesomeIcon icon={faTimes} />
            </Button>
        </AlertNotification.Root>
    );
};

export { OveragesWarningPrompt };
